import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DateRange, Locale } from "@/shared/utils";
import { matchesDateRange, normalizeDateToStartOfDay, normalizeDateToEndOfDay } from "@/shared/utils";
import { usePagination } from "@/shared/hooks";
import { useGetIncomeRecordsQuery } from "../api";
import type { IncomeType, IncomePool, IncomeRecordListItem, IncomeRecordsListQueryParams } from "../types";
import { useIncomeSort, toggleIncomeSort, type IncomeSortKey, type IncomeSortConfig } from "./useIncomeSort";

export interface PeriodRange {
  start: string;
  end: string;
}

export interface IncomeStats {
  monthlyAmount: string;
  quarterlyAmount: string;
  yearlyAmount: string;
  monthRange: PeriodRange;
  quarterRange: PeriodRange;
  yearRange: PeriodRange;
}

export interface PoolFilterOption {
  label: string;
  value: IncomePool | null;
}

export interface IncomeTypeOption {
  label: string;
  value: IncomeType | null;
}

export interface UseIncomeListPageReturn {
  data: IncomeRecordListItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
  search: string;
  setSearch: (value: string) => void;
  dateRange: DateRange;
  setDateRange: (value: DateRange) => void;
  poolFilter: IncomePool | null;
  setPoolFilter: (value: IncomePool | null) => void;
  incomeTypeFilter: IncomeType | null;
  setIncomeTypeFilter: (value: IncomeType | null) => void;
  poolOptions: PoolFilterOption[];
  incomeTypeOptions: IncomeTypeOption[];
  activeFilters: boolean;
  locale: Locale;
  filteredRecords: IncomeRecordListItem[];
  sortConfig: IncomeSortConfig | null;
  handleSort: (key: IncomeSortKey) => void;
  pagination: ReturnType<typeof usePagination>;
  pageRecords: IncomeRecordListItem[];
  handleResetFilters: () => void;
  stats: IncomeStats;
  t: ReturnType<typeof useTranslation>["t"];
}

export function useIncomeListPage(): UseIncomeListPageReturn {
  const { i18n, t } = useTranslation("IncomePage");
  const locale = i18n.language as Locale;

  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>({ start: null, end: null });
  const [poolFilter, setPoolFilter] = useState<IncomePool | null>(null);
  const [incomeTypeFilter, setIncomeTypeFilter] = useState<IncomeType | null>(null);
  const [sortConfig, setSortConfig] = useState<IncomeSortConfig | null>(null);

  const queryParams = useMemo<IncomeRecordsListQueryParams | undefined>(() => {
    const params: IncomeRecordsListQueryParams = {};
    if (incomeTypeFilter) params.income_type = incomeTypeFilter;
    if (dateRange.start) params.date_from = dateRange.start.toISOString().split("T")[0];
    if (dateRange.end) params.date_to = dateRange.end.toISOString().split("T")[0];
    return Object.keys(params).length > 0 ? params : undefined;
  }, [incomeTypeFilter, dateRange]);

  const { data, isLoading, isError } = useGetIncomeRecordsQuery(queryParams);
  const { data: allRecords } = useGetIncomeRecordsQuery();

  const poolOptions = useMemo<PoolFilterOption[]>(
    () => [
      { label: t("filters.allPools"), value: null },
      { label: t("poolLabels.internal"), value: "internal" },
      { label: t("poolLabels.external"), value: "external" },
    ],
    [t],
  );

  const incomeTypeOptions = useMemo<IncomeTypeOption[]>(
    () => [
      { label: t("filters.allIncomeTypes"), value: null },
      { label: t("incomeTypes.deposit_interest"), value: "deposit_interest" },
      { label: t("incomeTypes.grant"), value: "grant" },
      { label: t("incomeTypes.entrance_fee"), value: "entrance_fee" },
      { label: t("incomeTypes.membership_fee"), value: "membership_fee" },
      { label: t("incomeTypes.charity"), value: "charity" },
      { label: t("incomeTypes.sponsorship"), value: "sponsorship" },
      { label: t("incomeTypes.gratuitous"), value: "gratuitous" },
      { label: t("incomeTypes.government_social_order"), value: "government_social_order" },
      { label: t("incomeTypes.donation"), value: "donation" },
      { label: t("incomeTypes.rental"), value: "rental" },
      { label: t("incomeTypes.other"), value: "other" },
    ],
    [t],
  );

  const stats = useMemo<IncomeStats>(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    const quarterStartMonth = Math.floor(month / 3) * 3;

    const monthStart = `${year}-${String(month + 1).padStart(2, "0")}-01`;
    const monthEndDate = new Date(year, month + 1, 0);
    const monthEnd = `${year}-${String(month + 1).padStart(2, "0")}-${String(monthEndDate.getDate()).padStart(2, "0")}`;

    const quarterStart = `${year}-${String(quarterStartMonth + 1).padStart(2, "0")}-01`;
    const quarterEndDate = new Date(year, quarterStartMonth + 3, 0);
    const quarterEnd = `${year}-${String(quarterStartMonth + 3).padStart(2, "0")}-${String(quarterEndDate.getDate()).padStart(2, "0")}`;

    const yearStart = `${year}-01-01`;
    const yearEnd = `${year}-12-31`;

    const emptyStats: IncomeStats = {
      monthlyAmount: "0.00",
      quarterlyAmount: "0.00",
      yearlyAmount: "0.00",
      monthRange: { start: monthStart, end: monthEnd },
      quarterRange: { start: quarterStart, end: quarterEnd },
      yearRange: { start: yearStart, end: yearEnd },
    };

    if (!allRecords) return emptyStats;

    const confirmed = allRecords.filter((r) => r.status === "confirmed");

    function sumAmountsInRange(records: IncomeRecordListItem[], from: string, to: string): string {
      let totalCents = 0;
      for (const record of records) {
        if (record.document_date >= from && record.document_date <= to) {
          totalCents += Math.round(parseFloat(record.amount) * 100);
        }
      }
      return (totalCents / 100).toFixed(2);
    }

    return {
      monthlyAmount: sumAmountsInRange(confirmed, monthStart, monthEnd),
      quarterlyAmount: sumAmountsInRange(confirmed, quarterStart, quarterEnd),
      yearlyAmount: sumAmountsInRange(confirmed, yearStart, yearEnd),
      monthRange: { start: monthStart, end: monthEnd },
      quarterRange: { start: quarterStart, end: quarterEnd },
      yearRange: { start: yearStart, end: yearEnd },
    };
  }, [allRecords]);

  const activeFilters = useMemo(
    () =>
      !!search.trim() ||
      !!dateRange.start ||
      !!dateRange.end ||
      poolFilter !== null ||
      !!incomeTypeFilter,
    [search, dateRange, poolFilter, incomeTypeFilter],
  );

  function handleSort(key: IncomeSortKey) {
    setSortConfig((prev) => toggleIncomeSort(prev, key));
  }

  const filteredRecords = useMemo(() => {
    if (!data) return [];

    return data.filter((record) => {
      if (search) {
        const searchLower = search.toLowerCase();
        const matchesSearch =
          record.source_name.toLowerCase().includes(searchLower) ||
          record.document_number.toLowerCase().includes(searchLower) ||
          record.income_type_display.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      if (poolFilter && record.pool !== poolFilter) return false;

      if (dateRange.start || dateRange.end) {
        const docDate = new Date(record.document_date);
        const normalizedDate = normalizeDateToStartOfDay(docDate);
        const matches = matchesDateRange(normalizedDate, dateRange, normalizeDateToStartOfDay, normalizeDateToEndOfDay);
        if (!matches) return false;
      }

      return true;
    });
  }, [data, search, poolFilter, dateRange]);

  const sortedRecords = useIncomeSort(filteredRecords, sortConfig);
  const pagination = usePagination(sortedRecords.length, 10, [
    search,
    poolFilter,
    incomeTypeFilter,
    dateRange,
    sortConfig,
  ]);
  const pageRecords = sortedRecords.slice(pagination.startIndex, pagination.endIndex);

  function handleResetFilters() {
    setSearch("");
    setDateRange({ start: null, end: null });
    setPoolFilter(null);
    setIncomeTypeFilter(null);
    setSortConfig(null);
  }

  return {
    data,
    isLoading,
    isError,
    search,
    setSearch,
    dateRange,
    setDateRange,
    poolFilter,
    setPoolFilter,
    incomeTypeFilter,
    setIncomeTypeFilter,
    poolOptions,
    incomeTypeOptions,
    activeFilters,
    locale,
    filteredRecords,
    sortConfig,
    handleSort,
    pagination,
    pageRecords,
    handleResetFilters,
    stats,
    t,
  };
}
